// Identità sonora Fuma c'anduma — motore Web Audio sintetizzato.
// Palette coerente: timbri cristallini (sine/triangle), scala pentatonica, inviluppi brevi
// ed eleganti, filtro passa-basso condiviso. Nessuna dipendenza esterna, cross-platform (web + mobile-web).

let ctx = null;
let master = null;
let appOpenPlayed = false;

const KEY = "fuma_sound_enabled";
export const isSoundEnabled = () => localStorage.getItem(KEY) !== "0";
export const setSoundEnabled = (v) => localStorage.setItem(KEY, v ? "1" : "0");

function ensureCtx() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.45;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// Sblocca l'audio al primo gesto utente (policy autoplay).
export function unlockAudio() {
  const c = ensureCtx();
  if (c && c.state === "suspended") c.resume().catch(() => {});
}

function scheduleTone(c, dest, { freq, start, dur, type = "sine", gain = 0.35, glideTo = null, filter = 3200 }) {
  const osc = c.createOscillator();
  const g = c.createGain();
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = filter;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + 0.014);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(lp);
  lp.connect(g);
  g.connect(dest);
  osc.start(start);
  osc.stop(start + dur + 0.03);
}

function tone(c, opts) {
  scheduleTone(c, master, opts);
}

const N = {
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, B5: 987.77,
  C6: 1046.5, D6: 1174.7, E6: 1318.5, G6: 1568.0, A6: 1760.0,
  G4: 392.0, A4: 440.0, E4: 329.63, C4: 261.63,
};

// Motivo firma del brand (~1,5s): pad caldo + hook ascendente cristallino + coda shimmer.
function brandNotes(t) {
  return [
    { freq: N.C4, start: t, dur: 1.35, type: "sine", gain: 0.12, filter: 1400 },
    { freq: N.G5, start: t + 0.02, dur: 0.3, type: "triangle", gain: 0.34 },
    { freq: N.C6, start: t + 0.16, dur: 0.3, type: "triangle", gain: 0.34 },
    { freq: N.E6, start: t + 0.30, dur: 0.34, type: "triangle", gain: 0.32 },
    { freq: N.G6, start: t + 0.46, dur: 0.75, type: "sine", gain: 0.3, glideTo: N.A6 },
    { freq: N.C6, start: t + 0.60, dur: 0.7, type: "sine", gain: 0.14 },
    { freq: N.E6, start: t + 0.66, dur: 0.7, type: "sine", gain: 0.12 },
  ];
}

const BUILDERS = {
  app_open: (t) => brandNotes(t),
  brand_jingle: (t) => brandNotes(t),
  order_confirmed: (t) => [
    { freq: N.E5, start: t, dur: 0.16, type: "sine", gain: 0.4 },
    { freq: N.A5, start: t + 0.1, dur: 0.32, type: "sine", gain: 0.4 },
  ],
  order_cancelled: (t) => [
    { freq: N.A5, start: t, dur: 0.16, type: "triangle", gain: 0.32 },
    { freq: N.E5, start: t + 0.11, dur: 0.34, type: "triangle", gain: 0.3 },
  ],
  rider_assigned: (t) => [
    { freq: N.G5, start: t, dur: 0.14, type: "sine", gain: 0.36 },
  ],
  rider_incoming: (t) => [
    { freq: N.D5, start: t, dur: 0.13, type: "triangle", gain: 0.3 },
    { freq: N.D5, start: t + 0.2, dur: 0.13, type: "triangle", gain: 0.3 },
  ],
  rider_arrived: (t) => [
    { freq: N.E5, start: t, dur: 0.28, type: "sine", gain: 0.36 },
    { freq: N.B5, start: t + 0.02, dur: 0.3, type: "sine", gain: 0.24 },
  ],
  delivery_completed: (t) => [
    { freq: N.C5, start: t, dur: 0.14, type: "sine", gain: 0.34 },
    { freq: N.E5, start: t + 0.09, dur: 0.14, type: "sine", gain: 0.34 },
    { freq: N.G5, start: t + 0.18, dur: 0.16, type: "sine", gain: 0.34 },
    { freq: N.C6, start: t + 0.27, dur: 0.4, type: "triangle", gain: 0.3 },
  ],
  mission_completed: (t) => [
    { freq: N.C5, start: t, dur: 0.12, type: "triangle", gain: 0.32 },
    { freq: N.E5, start: t + 0.08, dur: 0.12, type: "triangle", gain: 0.32 },
    { freq: N.G5, start: t + 0.16, dur: 0.12, type: "triangle", gain: 0.32 },
    { freq: N.C6, start: t + 0.24, dur: 0.14, type: "sine", gain: 0.32 },
    { freq: N.E6, start: t + 0.34, dur: 0.5, type: "sine", gain: 0.3 },
  ],
  level_up: (t) => [
    { freq: N.C5, start: t, dur: 0.16, type: "sine", gain: 0.36, glideTo: N.D5 },
    { freq: N.G5, start: t + 0.12, dur: 0.16, type: "sine", gain: 0.36 },
    { freq: N.C6, start: t + 0.24, dur: 0.5, type: "triangle", gain: 0.32, glideTo: N.D6 },
  ],
  badge: (t) => [
    { freq: N.C6, start: t, dur: 0.22, type: "sine", gain: 0.34 },
    { freq: N.G6, start: t + 0.06, dur: 0.4, type: "sine", gain: 0.24 },
  ],
  credits: (t) => [
    { freq: N.A5, start: t, dur: 0.1, type: "square", gain: 0.22, filter: 2600 },
    { freq: N.C6, start: t + 0.09, dur: 0.14, type: "square", gain: 0.22, filter: 2600 },
  ],
  ai_notify: (t) => [
    { freq: N.E5, start: t, dur: 0.3, type: "triangle", gain: 0.24, filter: 2400 },
  ],
  error: (t) => [
    { freq: N.G4, start: t, dur: 0.16, type: "sine", gain: 0.3 },
    { freq: N.E4, start: t + 0.12, dur: 0.24, type: "sine", gain: 0.3 },
  ],
  success: (t) => [
    { freq: N.E5, start: t, dur: 0.14, type: "sine", gain: 0.38 },
    { freq: N.A5, start: t + 0.1, dur: 0.3, type: "sine", gain: 0.38 },
  ],
  notification: (t) => [
    { freq: N.A5, start: t, dur: 0.28, type: "sine", gain: 0.3 },
  ],
  login: (t) => [
    { freq: N.E5, start: t, dur: 0.12, type: "sine", gain: 0.3 },
    { freq: N.A5, start: t + 0.09, dur: 0.28, type: "sine", gain: 0.32 },
  ],
  register: (t) => [
    { freq: N.C5, start: t, dur: 0.12, type: "triangle", gain: 0.3 },
    { freq: N.E5, start: t + 0.09, dur: 0.12, type: "triangle", gain: 0.3 },
    { freq: N.G5, start: t + 0.18, dur: 0.34, type: "sine", gain: 0.32 },
  ],
  welcome: (t) => brandNotes(t),
};

// Registry per FILE audio esterni (opzionali). L'utente potrà aggiungerli in seguito:
// registerSoundFile("order_confirmed", "/sounds/order.mp3"). Finché non è registrato,
// resta il suono sintetico attuale (fallback). Nessun costo/latenza extra di default.
const FILES = {};
export function registerSoundFile(name, url) {
  try {
    const a = new Audio(url);
    a.preload = "auto";
    FILES[name] = a;
  } catch (e) { /* noop */ }
}
export function registerSoundFiles(map = {}) {
  Object.entries(map).forEach(([k, url]) => registerSoundFile(k, url));
}


export const SOUND_KEYS = Object.keys(BUILDERS);

export function play(name) {
  if (!isSoundEnabled()) return;
  // Se per questo evento è stato registrato un file audio esterno, riproducilo (con fallback sintetico).
  const file = FILES[name];
  if (file) {
    try { const a = file.cloneNode(); a.volume = 0.7; a.play().catch(() => {}); return; } catch (e) { /* fallback */ }
  }
  const c = ensureCtx();
  if (!c) return;
  const build = BUILDERS[name] || BUILDERS.notification;
  const t = c.currentTime + 0.02;
  try {
    build(t).forEach((n) => tone(c, n));
  } catch (e) { /* noop */ }
}

// app_open una sola volta per sessione (dopo lo sblocco al primo gesto).
export function playAppOpenOnce() {
  if (appOpenPlayed) return;
  appOpenPlayed = true;
  play("app_open");
}

export function playBrandJingle() {
  play("brand_jingle");
}

function encodeWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const sr = buffer.sampleRate;
  const total = buffer.length * numCh * 2 + 44;
  const ab = new ArrayBuffer(total);
  const view = new DataView(ab);
  const wr = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  wr(0, "RIFF"); view.setUint32(4, total - 8, true); wr(8, "WAVE"); wr(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, numCh, true);
  view.setUint32(24, sr, true); view.setUint32(28, sr * numCh * 2, true);
  view.setUint16(32, numCh * 2, true); view.setUint16(34, 16, true); wr(36, "data");
  view.setUint32(40, total - 44, true);
  let off = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numCh; ch++) {
      const s = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      off += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}

// Renderizza il logo audio offline e avvia il download di un file WAV (per video/social).
export async function downloadBrandJingle() {
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!OAC) return;
  const sr = 44100;
  const off = new OAC(1, Math.ceil(sr * 1.6), sr);
  const g = off.createGain();
  g.gain.value = 0.6;
  g.connect(off.destination);
  brandNotes(0.02).forEach((n) => scheduleTone(off, g, n));
  const rendered = await off.startRendering();
  const url = URL.createObjectURL(encodeWav(rendered));
  const a = document.createElement("a");
  a.href = url;
  a.download = "fuma-canduma-logo-audio.wav";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
